import { LoaderCircle, ScanLine, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { FORMATOS_CODIGO, soportaBarcodeDetectorNativo } from "@/lib/barcode";

// Tipo mínimo de la API nativa BarcodeDetector (todavía no está en las libs de TS del DOM). Solo se
// declara lo que se usa: construir con formatos y detectar sobre una fuente de imagen (el vídeo).
type CodigoDetectado = { rawValue: string };
interface BarcodeDetectorLike {
  detect(source: CanvasImageSource): Promise<CodigoDetectado[]>;
}
type BarcodeDetectorCtor = new (opts?: { formats?: string[] }) => BarcodeDetectorLike;

/**
 * BarcodeScanner abre la cámara trasera a pantalla completa y detecta un QR / código de barras,
 * devolviendo su texto por `onDetected` en la PRIMERA lectura. Dos rutas: BarcodeDetector nativo
 * (Chromium/Android, rápido y sin dependencias) y, si no está, un fallback @zxing/browser cargado
 * bajo demanda (import dinámico → chunk aparte, no infla el bundle principal). Libera la cámara y el
 * bucle de detección al cerrar o al detectar. Ayuda de captura para no teclear con guantes en campo
 * (#54): es de solo lectura, no genera ninguna mutación.
 */
export function BarcodeScanner({
  open,
  titulo = "Escanear código",
  onDetected,
  onClose,
}: {
  open: boolean;
  titulo?: string;
  onDetected: (codigo: string) => void;
  onClose: () => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [cargando, setCargando] = useState(true);

  // Guarda el callback en una ref para que el efecto de la cámara dependa solo de `open`: así un
  // re-render del padre (que reconstruya `onDetected`) no reinicia la cámara a mitad del escaneo.
  const onDetectedRef = useRef(onDetected);
  onDetectedRef.current = onDetected;

  useEffect(() => {
    if (!open) {
      return;
    }
    let cancelado = false;
    let stream: MediaStream | null = null;
    let raf = 0;
    let zxingControls: { stop: () => void } | null = null;
    setError(null);
    setCargando(true);

    const limpiar = (): void => {
      cancelAnimationFrame(raf);
      try {
        zxingControls?.stop();
      } catch {
        // best-effort: liberar la cámara no debe lanzar.
      }
      for (const track of stream?.getTracks() ?? []) {
        track.stop();
      }
      stream = null;
    };

    // detectar centraliza el «primera lectura gana»: para todo y emite el código una sola vez.
    const detectar = (codigo: string): void => {
      if (cancelado || !codigo) {
        return;
      }
      cancelado = true;
      limpiar();
      onDetectedRef.current(codigo);
    };

    const arrancar = async (): Promise<void> => {
      const video = videoRef.current;
      if (!video) {
        return;
      }
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment" },
          audio: false,
        });
        if (cancelado) {
          for (const track of stream.getTracks()) {
            track.stop();
          }
          return;
        }
        video.srcObject = stream;
        await video.play();
        setCargando(false);

        // Ruta rápida: BarcodeDetector nativo. Se sondea cada frame con requestAnimationFrame.
        if (soportaBarcodeDetectorNativo()) {
          const Ctor = (window as unknown as { BarcodeDetector: BarcodeDetectorCtor })
            .BarcodeDetector;
          const detector = new Ctor({ formats: [...FORMATOS_CODIGO] });
          const tick = async (): Promise<void> => {
            if (cancelado) {
              return;
            }
            try {
              const codigos = await detector.detect(video);
              const valor = codigos[0]?.rawValue;
              if (valor) {
                detectar(valor);
                return;
              }
            } catch {
              // Un frame ilegible no es un error: se reintenta en el siguiente.
            }
            raf = requestAnimationFrame(() => void tick());
          };
          raf = requestAnimationFrame(() => void tick());
          return;
        }

        // Fallback: @zxing/browser (import dinámico → no infla el bundle principal).
        const { BrowserMultiFormatReader } = await import("@zxing/browser");
        if (cancelado) {
          return;
        }
        const reader = new BrowserMultiFormatReader();
        zxingControls = await reader.decodeFromVideoElement(video, (result) => {
          const texto = result?.getText();
          if (texto) {
            detectar(texto);
          }
        });
      } catch {
        if (!cancelado) {
          setCargando(false);
          setError(
            "No se pudo acceder a la cámara. Comprueba los permisos o escribe el código a mano.",
          );
        }
      }
    };

    void arrancar();
    return () => {
      cancelado = true;
      limpiar();
    };
  }, [open]);

  if (!open) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-black">
      <div className="flex items-center justify-between px-4 py-3 text-white">
        <span className="flex items-center gap-2 font-semibold">
          <ScanLine className="h-5 w-5" aria-hidden="true" />
          {titulo}
        </span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Cerrar escáner"
          className="rounded-full p-2 hover:bg-white/10"
        >
          <X className="h-6 w-6" aria-hidden="true" />
        </button>
      </div>

      <div className="relative flex flex-1 items-center justify-center overflow-hidden">
        <video
          ref={videoRef}
          className="h-full w-full object-cover"
          playsInline
          muted
          aria-label="Vista de la cámara"
        />
        {cargando && !error ? (
          <div className="absolute inset-0 flex items-center justify-center text-white">
            <LoaderCircle className="h-8 w-8 animate-spin" aria-hidden="true" />
          </div>
        ) : null}
        {!cargando && !error ? (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div className="h-56 w-56 rounded-2xl border-4 border-white/80 shadow-[0_0_0_9999px_rgba(0,0,0,0.45)]" />
          </div>
        ) : null}
      </div>

      {error ? (
        <div className="bg-black px-6 pb-8 text-center">
          <p className="mb-4 text-white/90">{error}</p>
          <Button variant="outline" onClick={onClose}>
            Volver
          </Button>
        </div>
      ) : (
        <p className="bg-black px-6 pb-8 text-center text-sm text-white/70">
          Apunta la cámara al código del candado. Se seleccionará automáticamente.
        </p>
      )}
    </div>
  );
}
